import { getSessionStorage, setSessionStorage, trackCustomEvent } from '@healthhub/react-ui';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { SourceCodeType } from '@shared/constants/constants';
import { EVENT_NAMES } from '@shared/constants/eventTrackers';
import { useLayoutViewContext } from '@shared/context';
import { useAppSelector } from '@shared/stores/rootTypes';
import type { ISelectedProfile } from '@shared/stores/user/user.types';
import { selectedProfileSelector } from '@shared/stores/user/userSelector';
import { userAction } from '@shared/stores/user/userSlice';
import { getDisplayName } from '@shared/utils/displayName';

import { useCurrentUserOrCurrentFederatedUser } from '../useCurrentUserOrCurrentFederatedUser';
import useRelatedPeople from '../useRelatedPeople';

interface Props {
  isIncludedCaregivers?: boolean;
}

const useMemberIdentifier = (props?: Props) => {
  const { isPortalView } = useLayoutViewContext();

  const isIncludedCaregivers = props?.isIncludedCaregivers;

  const selectedProfile = useAppSelector(selectedProfileSelector);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { userProfile } = useCurrentUserOrCurrentFederatedUser({
    isPatientMasterInfoRequired: true,
  });

  const queryMemberIdentifier = searchParams.get('mid');

  const memberIdentifier = selectedProfile?.memberIdentifier ?? '';

  const { data: userFamily, isFetching: isFetchingUserFamily } = useRelatedPeople({
    skip: !queryMemberIdentifier,
  });

  useEffect(() => {
    // TODO: Update this in HH+ constant for session storage

    if (memberIdentifier) {
      const memberPrn = getSessionStorage(EVENT_NAMES.MEMBER_PRN);
      if (memberPrn === memberIdentifier) return;

      setSessionStorage(EVENT_NAMES.MEMBER_PRN, memberIdentifier);
      trackCustomEvent(EVENT_NAMES.MEMBER_PRN, {
        prn: memberIdentifier,
      });
    }
  }, [memberIdentifier]);

  const handleSelectedProfile = useCallback(
    (payload: ISelectedProfile) => {
      dispatch(userAction.setSelectedProfile(payload));
    },
    [dispatch]
  );

  const setDefaultUserProfile = useCallback(() => {
    handleSelectedProfile({
      memberIdentifier: '',
      name: getDisplayName(userProfile),
      nric: userProfile?.MaskedNric ?? '',
      photo: userProfile?.Photo?.Data ?? '',
      sourceCode: '',
    });
  }, [handleSelectedProfile, userProfile]);

  const getSelectedFamilyMember = useCallback(() => {
    if (!userFamily) return { selectedFamilyMember: null, sourceCode: '' };

    const selectedCareDependantsProfile = userFamily.FamilyAndFriend?.find(
      (profile) => profile.Prn === queryMemberIdentifier
    );

    const selectedCaregiverProfile = isIncludedCaregivers
      ? userFamily.Caregivers?.find((profile) => profile.Prn === queryMemberIdentifier)
      : undefined;

    const selectedChildProfile = userFamily.Children?.find((profile) => profile.Prn === queryMemberIdentifier);

    const selectedFamilyMember = selectedCaregiverProfile ?? selectedCareDependantsProfile ?? selectedChildProfile;

    return {
      selectedFamilyMember,
      sourceCode: selectedCareDependantsProfile ? SourceCodeType.FAMILY_FRIEND : SourceCodeType.CHILDREN,
    };
  }, [isIncludedCaregivers, queryMemberIdentifier, userFamily]);

  const navigateToHomePage = useCallback(() => {
    if (isPortalView) {
      navigate('/', { replace: true });
    }
  }, [navigate, isPortalView]);

  useEffect(() => {
    if (!queryMemberIdentifier) {
      setDefaultUserProfile();
      return;
    }

    if (queryMemberIdentifier === userProfile?.Prn) {
      setDefaultUserProfile();

      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('mid');
      navigate({ search: nextParams.toString(), hash: location.hash }, { replace: true, state: location.state });
      return;
    }

    if (isFetchingUserFamily) return;

    const { selectedFamilyMember, sourceCode } = getSelectedFamilyMember();

    if (selectedFamilyMember?.Prn) {
      handleSelectedProfile({
        age: Number(selectedFamilyMember.Age),
        displayAge: selectedFamilyMember.DisplayAge ?? '',
        gender: selectedFamilyMember.Gender as string,
        id: selectedFamilyMember.Id,
        memberIdentifier: selectedFamilyMember.Prn,
        name: selectedFamilyMember.DisplayName,
        nric: selectedFamilyMember.MaskedNric,
        photo: selectedFamilyMember.Photo?.Data ?? '',
        relationship: selectedFamilyMember.RelationShip ?? '',
        sourceCode,
      });
    } else {
      navigateToHomePage();
    }
  }, [
    queryMemberIdentifier,
    isFetchingUserFamily,
    getSelectedFamilyMember,
    handleSelectedProfile,
    setDefaultUserProfile,
    navigateToHomePage,
    userProfile?.Prn,
    searchParams,
    navigate,
    location.hash,
    location.state,
  ]);

  return { isFetchingUserFamily, memberIdentifier, queryMemberIdentifier };
};

export default useMemberIdentifier;
